import type { I18nString } from '@affine/i18n';

import type { QuickSearchGroup } from './group';

/** What a hit is; rows show it as a miniature instead of a text badge. */
export type QuickSearchHitKind =
  | 'doc'
  | 'text'
  | 'image'
  | 'canvas'
  | 'sticky'
  | 'attachment'
  | 'pdf'
  | 'link'
  | 'database'
  | 'automation';

/** How a hit kind is named, e.g. as a place under its doc. */
export const QUICK_SEARCH_HIT_KIND_LABEL: Record<QuickSearchHitKind, string> = {
  doc: 'com.streamlain.quicksearch.sourceType.doc',
  text: 'com.streamlain.quicksearch.sourceType.text',
  image: 'com.streamlain.quicksearch.sourceType.image',
  canvas: 'com.streamlain.quicksearch.sourceType.edgeless',
  sticky: 'com.streamlain.quicksearch.sourceType.sticky',
  attachment: 'com.streamlain.quicksearch.sourceType.attachment',
  pdf: 'com.streamlain.quicksearch.sourceType.pdf',
  link: 'com.streamlain.quicksearch.sourceType.embed',
  database: 'com.streamlain.quicksearch.sourceType.database',
  automation: 'com.streamlain.quicksearch.sourceType.automation',
};

/**
 * A row of a doc's result group: the doc (the container the user opens), one
 * place inside it where the query was found, or "Show N more" places.
 */
export type QuickSearchResultRow = 'doc' | 'place' | 'more';

export type QuickSearchHitVisual = {
  kind: QuickSearchHitKind;
  /** Image hits: the blob drawn as the miniature. */
  blobId?: string;
};

export type QuickSearchItem<S = any, P = any> = {
  id: string;
  source: S;
  label:
    | I18nString
    | {
        title: I18nString;
        subTitle?: I18nString;
      };
  score?: number;
  icon?: React.ReactNode | React.ComponentType;
  group?: QuickSearchGroup;
  disabled?: boolean;
  keyBinding?: string;
  timestamp?: number;
  /** Set on the rows of a doc's result group; other rows stand alone. */
  row?: QuickSearchResultRow;
  badge?: I18nString;
  visual?: QuickSearchHitVisual;
  /** The hit lives in a doc that is in the trash. */
  trashed?: boolean;
  /**
   * Offline only: the doc has no full text on this device (`unavailable`) or
   * was just made available offline and loads online (`pending`).
   */
  offline?: 'unavailable' | 'pending';
  payload?: P;
  beforeSubmit?: () => boolean;
} & (P extends NonNullable<unknown> ? { payload: P } : unknown);

/** Content results already have their own normal rows, not duplicate command rows. */
export const QUICK_SEARCH_RESULT_OWNER = 'quicksearch-results';
export function quickSearchResultDefinitionId(
  generation: number,
  itemId: string
) {
  return JSON.stringify([generation, itemId]);
}
